import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://nlpd.jagerdori.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

interface SeoProps {
  title: string;
  description: string;
  /** Override the canonical path. Defaults to current pathname. */
  canonicalPath?: string;
  /**
   * Absolute URL of the share image. Defaults to /og-image.png at the
   * site root. Pass a per-page override for pages that warrant their own
   * preview (e.g. a richly branded lead-magnet card).
   */
  image?: string;
  noIndex?: boolean;
}

function setMeta(selector: string, attr: "content", value: string, create?: () => HTMLMetaElement) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el && create) {
    el = create();
    document.head.appendChild(el);
  }
  if (el) el.setAttribute(attr, value);
  return el;
}

const Seo = ({ title, description, canonicalPath, image, noIndex }: SeoProps) => {
  const location = useLocation();
  const path = canonicalPath ?? location.pathname;
  const canonical = `${SITE_URL}${path === "/" ? "/" : path.replace(/\/$/, "")}`;
  const resolvedImage = image && /^https?:\/\//i.test(image)
    ? image
    : image
      ? `${SITE_URL}${image.startsWith("/") ? image : `/${image}`}`
      : DEFAULT_OG_IMAGE;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const prevDesc =
      document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.content ?? "";

    setMeta('meta[name="description"]', "content", description, () => {
      const m = document.createElement("meta");
      m.name = "description";
      return m;
    });
    setMeta('meta[property="og:title"]', "content", title, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:title");
      return m;
    });
    setMeta('meta[property="og:description"]', "content", description, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:description");
      return m;
    });
    setMeta('meta[property="og:url"]', "content", canonical, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:url");
      return m;
    });
    setMeta('meta[property="og:image"]', "content", resolvedImage, () => {
      const m = document.createElement("meta");
      m.setAttribute("property", "og:image");
      return m;
    });
    setMeta('meta[name="twitter:title"]', "content", title, () => {
      const m = document.createElement("meta");
      m.name = "twitter:title";
      return m;
    });
    setMeta('meta[name="twitter:description"]', "content", description, () => {
      const m = document.createElement("meta");
      m.name = "twitter:description";
      return m;
    });
    setMeta('meta[name="twitter:image"]', "content", resolvedImage, () => {
      const m = document.createElement("meta");
      m.name = "twitter:image";
      return m;
    });
    setMeta('meta[name="twitter:card"]', "content", "summary_large_image", () => {
      const m = document.createElement("meta");
      m.name = "twitter:card";
      return m;
    });

    // Canonical: ensure exactly one
    const existingCanonicals = document.head.querySelectorAll('link[rel="canonical"]');
    existingCanonicals.forEach((el, i) => i > 0 && el.remove());
    let linkEl = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!linkEl) {
      linkEl = document.createElement("link");
      linkEl.rel = "canonical";
      document.head.appendChild(linkEl);
    }
    const prevCanonical = linkEl.href;
    linkEl.href = canonical;

    // Robots noindex
    let robotsEl = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]');
    const hadRobots = !!robotsEl;
    const prevRobots = robotsEl?.content;
    if (noIndex) {
      if (!robotsEl) {
        robotsEl = document.createElement("meta");
        robotsEl.name = "robots";
        document.head.appendChild(robotsEl);
      }
      robotsEl.content = "noindex, nofollow";
    } else if (robotsEl) {
      robotsEl.content = "index, follow";
    }

    return () => {
      document.title = prevTitle;
      const desc = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
      if (desc && prevDesc) desc.content = prevDesc;
      if (linkEl && prevCanonical) linkEl.href = prevCanonical;
      if (noIndex && robotsEl) {
        if (!hadRobots) robotsEl.remove();
        else if (prevRobots) robotsEl.content = prevRobots;
      }
    };
  }, [title, description, canonical, resolvedImage, noIndex]);

  return null;
};

export default Seo;
